import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Typography,
  Box,
} from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import {
  TaskTemplatesClient,
  TaskTemplateDto,
  TaskTemplateVersionDto,
  TaskCategoriesClient,
  TaskCategoryDto,
  TaskPrioritiesClient,
  TaskPriorityDto,
  TaskSchedulesClient,
  TaskScheduleDto,
} from '../../app/services/api/tasksClient';
import type { TaskTemplateStepDto, StepType, EvidenceType } from '../../app/services/api/tasksClient';
import { Pill } from '../../components/shared/Pill';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import DocumentLinker from '../../components/forms/DocumentLinker';

const templatesClient = TaskTemplatesClient;
const categoriesClient = TaskCategoriesClient;
const prioritiesClient = TaskPrioritiesClient;
const schedulesClient = TaskSchedulesClient;

const TaskTemplateDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);

  const isCreate = !id || id === 'new';
  const canCreate = hasPermissionTo ? hasPermissionTo(['TaskTemplatesCreate']) : false;
  const canEdit = hasPermissionTo ? hasPermissionTo(['TaskTemplatesEdit']) : false;

  const [categories, setCategories] = useState<TaskCategoryDto[]>([]);
  const [priorities, setPriorities] = useState<TaskPriorityDto[]>([]);
  const [schedules, setSchedules] = useState<TaskScheduleDto[]>([]);
  const [template, setTemplate] = useState<TaskTemplateDto | null>(null);
  const [formValues, setFormValues] = useState<{
    name: string;
    description: string;
    taskCategoryId: number | '';
  }>({
    name: '',
    description: '',
    taskCategoryId: '',
  });

  const [selectedVersionId, setSelectedVersionId] = useState<number | ''>('');
  const [versionFormValues, setVersionFormValues] = useState<{
    defaultTaskPriorityId: number | '';
    expectedDurationMins: number | '';
    isActive: boolean;
    isGlobalTemplate: boolean;
    requiresApproval: boolean;
  }>({
    defaultTaskPriorityId: '',
    expectedDurationMins: '',
    isActive: true,
    isGlobalTemplate: true,
    requiresApproval: false,
  });
  const [steps, setSteps] = useState<TaskTemplateStepDto[]>([]);
  const [initialVersionState, setInitialVersionState] = useState<string | null>(null);

  const [isStepModalOpen, setIsStepModalOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<TaskTemplateStepDto | null>(null);
  const [stepFormValues, setStepFormValues] = useState<{
    stepType: StepType;
    instruction: string;
    linkedDocumentId: number | null;
    linkedDocumentName: string | null;
    linkedUrl: string;
    linkedUrlLabel: string;
    evidenceRequired: boolean;
    evidenceType: EvidenceType | '';
  }>({
    stepType: 'text',
    instruction: '',
    linkedDocumentId: null,
    linkedDocumentName: null,
    linkedUrl: '',
    linkedUrlLabel: '',
    evidenceRequired: false,
    evidenceType: '',
  });

  const evidenceTypeOptions: { value: EvidenceType; label: string }[] = [
    { value: 'photo', label: 'Photo' },
    { value: 'signature', label: 'Signature' },
    { value: 'text', label: 'Text Note' },
    { value: 'document', label: 'Document Upload' },
    { value: 'checkbox', label: 'Checkbox Confirmation' },
    { value: 'numeric', label: 'Numeric Value' },
    { value: 'financial', label: 'Financial Value' },
  ];
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const operationalDaysCount = 0; // TODO: wire to OperationalDays/Overview APIs when available

  const [isDirty, setIsDirty] = useState(false);
  const [pendingVersionChangeId, setPendingVersionChangeId] = useState<number | null>(null);
  const [pendingDuplicate, setPendingDuplicate] = useState(false);
  const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] = useState(false);
  const [isDeleteStepDialogOpen, setIsDeleteStepDialogOpen] = useState(false);
  const [stepIdPendingDelete, setStepIdPendingDelete] = useState<number | null>(null);

  useEffect(() => {
    categoriesClient.getCategories().then(setCategories).catch(() => setCategories([]));
    prioritiesClient.getPriorities().then(setPriorities).catch(() => setPriorities([]));

    if (!isCreate && id) {
      const templateId = Number(id);
      templatesClient.getTemplate(templateId).then(t => {
        setTemplate(t);
        setFormValues({
          name: t.name,
          description: t.description ?? '',
          taskCategoryId: t.taskCategoryId ?? '',
        });

        const activeVersion = t.versions.find(v => v.isActive) ?? t.versions[0];
        if (activeVersion) {
          setSelectedVersionId(activeVersion.id);
          const initialVersionForm: {
            defaultTaskPriorityId: number | '';
            expectedDurationMins: number | '';
            isActive: boolean;
            isGlobalTemplate: boolean;
            requiresApproval: boolean;
          } = {
            defaultTaskPriorityId: activeVersion.defaultTaskPriorityId ?? '',
            expectedDurationMins:
              activeVersion.expectedDurationMins === null ||
                activeVersion.expectedDurationMins === undefined
                ? ''
                : activeVersion.expectedDurationMins,
            isActive: activeVersion.isActive,
            isGlobalTemplate: activeVersion.isGlobalTemplate,
            requiresApproval: activeVersion.requiresApproval ?? false,
          };
          const initialSteps = (activeVersion.steps ?? []).slice().sort((a, b) => a.order - b.order);
          setVersionFormValues(initialVersionForm);
          setSteps(initialSteps);
          setInitialVersionState(
            JSON.stringify({
              versionFormValues: initialVersionForm,
              steps: initialSteps,
            }),
          );
        }
      }).catch(() => setTemplate(null));

      schedulesClient.getSchedules().then(all => {
        setSchedules(all.filter(s => s.taskTemplateId === templateId));
      }).catch(() => setSchedules([]));
    } else {
      // New template: initialise on-screen model with a single inactive version
      const initialVersion: TaskTemplateVersionDto = {
        id: 1,
        templateId: 0,
        versionNumber: 1,
        defaultTaskPriorityId: 0,
        expectedDurationMins: 0,
        isActive: false,
        isGlobalTemplate: true,
        steps: [],
        requiresApproval: false,
      };

      const initialTemplate: TaskTemplateDto = {
        id: 0,
        name: '',
        description: '',
        taskCategoryId: null,
        versions: [initialVersion],
      };

      setTemplate(initialTemplate);
      setSelectedVersionId(initialVersion.id);

      const initialVersionForm: {
        defaultTaskPriorityId: number | '';
        expectedDurationMins: number | '';
        isActive: boolean;
        isGlobalTemplate: boolean;
        requiresApproval: boolean;
      } = {
        defaultTaskPriorityId: '',
        expectedDurationMins: '',
        isActive: false,
        isGlobalTemplate: true,
        requiresApproval: false,
      };

      setVersionFormValues(initialVersionForm);
      setSteps([]);
      setInitialVersionState(
        JSON.stringify({
          versionFormValues: initialVersionForm,
          steps: [],
        }),
      );
    }
  }, [id, isCreate]);

  useEffect(() => {
    if (!template || initialVersionState === null) {
      return;
    }
    const currentState = JSON.stringify({
      versionFormValues,
      steps,
    });
    setIsDirty(currentState !== initialVersionState);
  }, [template, versionFormValues, steps, initialVersionState]);

  const selectedVersion: TaskTemplateVersionDto | undefined = useMemo(() => {
    if (!template || selectedVersionId === '') return undefined;
    return template.versions.find(v => v.id === selectedVersionId);
  }, [template, selectedVersionId]);

  if (isCreate && !canCreate) {
    return (
      <PageArea>
        <PageTitle>Task Template</PageTitle>
        <p>You do not have permission to create task templates.</p>
      </PageArea>
    );
  }

  if (!isCreate && !canEdit) {
    return (
      <PageArea>
        <PageTitle>Task Template</PageTitle>
        <p>You do not have permission to edit task templates.</p>
      </PageArea>
    );
  }

  const validate = () => {
    const e: { [key: string]: string } = {};
    if (!formValues.name.trim()) e.name = 'Required';
    if (!versionFormValues.defaultTaskPriorityId) e.defaultTaskPriorityId = 'Required';
    if (
      versionFormValues.expectedDurationMins !== '' &&
      Number.isNaN(Number(versionFormValues.expectedDurationMins))
    ) {
      e.expectedDurationMins = 'Invalid number';
    }
    if (steps.length === 0) {
      e.steps = 'At least one step is required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    if (!template) return;

    const orderedSteps = steps.map((s, index) => ({
      ...s,
      order: index + 1,
    }));
    if (isCreate) {
      const createDto: TaskTemplateDto = {
        id: 0,
        name: formValues.name,
        description: formValues.description,
        taskCategoryId:
          formValues.taskCategoryId === '' ? null : Number(formValues.taskCategoryId),
        versions: [
          {
            id: 0,
            templateId: 0,
            versionNumber: 1,
            defaultTaskPriorityId: Number(versionFormValues.defaultTaskPriorityId),
            expectedDurationMins:
              versionFormValues.expectedDurationMins === ''
                ? null
                : Number(versionFormValues.expectedDurationMins),
            isActive: versionFormValues.isActive,
            isGlobalTemplate: versionFormValues.isGlobalTemplate,
            requiresApproval: versionFormValues.requiresApproval,
            steps: orderedSteps,
          },
        ],
      };

      await templatesClient.createTemplate(createDto);
      navigate('/task-templates');
      return;
    }

    const updatedVersions = template.versions.map(v =>
      v.id === selectedVersionId
        ? {
          ...v,
          defaultTaskPriorityId: Number(versionFormValues.defaultTaskPriorityId),
          expectedDurationMins:
            versionFormValues.expectedDurationMins === ''
              ? null
              : Number(versionFormValues.expectedDurationMins),
          isActive: versionFormValues.isActive,
          isGlobalTemplate: versionFormValues.isGlobalTemplate,
          requiresApproval: versionFormValues.requiresApproval,
          steps: orderedSteps,
        }
        : v,
    );

    const dto: TaskTemplateDto = {
      ...template,
      name: formValues.name,
      description: formValues.description,
      taskCategoryId:
        formValues.taskCategoryId === '' ? null : Number(formValues.taskCategoryId),
      versions: updatedVersions,
    };

    await templatesClient.updateTemplate(template.id, dto);
    setTemplate(dto);
    setIsDirty(false);
    navigate('/task-templates');
  };

  const title = isCreate ? 'Create Task Template' : 'Edit Task Template';

  const handleVersionChangeInternal = (value: number) => {
    if (!template) return;
    const v = template.versions.find(x => x.id === value);
    if (!v) return;
    setSelectedVersionId(v.id);
    const newVersionForm: {
      defaultTaskPriorityId: number | '';
      expectedDurationMins: number | '';
      isActive: boolean;
      isGlobalTemplate: boolean;
      requiresApproval: boolean;
    } = {
      defaultTaskPriorityId: v.defaultTaskPriorityId ?? '',
      expectedDurationMins:
        v.expectedDurationMins === null || v.expectedDurationMins === undefined
          ? ''
          : v.expectedDurationMins,
      isActive: v.isActive,
      isGlobalTemplate: v.isGlobalTemplate,
      requiresApproval: v.requiresApproval ?? false,
    };
    setVersionFormValues(newVersionForm);
    const newSteps = (v.steps ?? []).slice().sort((a, b) => a.order - b.order);
    setSteps(newSteps);
    setInitialVersionState(
      JSON.stringify({
        versionFormValues: newVersionForm,
        steps: newSteps,
      }),
    );
    setIsDirty(false);
  };

  const handleVersionChange = (value: number) => {
    if (isDirty) {
      setPendingVersionChangeId(value);
      setIsUnsavedChangesDialogOpen(true);
    } else {
      handleVersionChangeInternal(value);
    }
  };

  const handleDuplicateVersionInternal = () => {
    if (!template || !selectedVersionId) return;
    const sourceVersion = template.versions.find(v => v.id === selectedVersionId);
    if (!sourceVersion) return;

    const maxId = Math.max(...template.versions.map(v => v.id), 0);

    const isEditingActive = sourceVersion.isActive;
    const baseVersionData = isEditingActive
      ? {
        ...sourceVersion,
        defaultTaskPriorityId: Number(versionFormValues.defaultTaskPriorityId),
        expectedDurationMins:
          versionFormValues.expectedDurationMins === ''
            ? null
            : Number(versionFormValues.expectedDurationMins),
        steps: steps.map((s, index) => ({ ...s, order: index + 1 })),
      }
      : sourceVersion;

    const newVersion: TaskTemplateVersionDto = {
      ...baseVersionData,
      id: maxId + 1,
      isActive: false,
      versionNumber: (sourceVersion.versionNumber ?? 1) + 1,
    };

    const updated: TaskTemplateDto = {
      ...template,
      versions: [...template.versions, newVersion],
    };
    setTemplate(updated);
    setSelectedVersionId(newVersion.id);
    setVersionFormValues({
      defaultTaskPriorityId: newVersion.defaultTaskPriorityId ?? '',
      expectedDurationMins:
        newVersion.expectedDurationMins === null ||
          newVersion.expectedDurationMins === undefined
          ? ''
          : newVersion.expectedDurationMins,
      isActive: newVersion.isActive,
      isGlobalTemplate: newVersion.isGlobalTemplate,
      requiresApproval: newVersion.requiresApproval ?? false,
    });
    const sortedNewSteps = (newVersion.steps ?? []).slice().sort((a, b) => a.order - b.order);
    setSteps(sortedNewSteps);
    setInitialVersionState(
      JSON.stringify({
        versionFormValues: {
          defaultTaskPriorityId: newVersion.defaultTaskPriorityId ?? '',
          expectedDurationMins:
            newVersion.expectedDurationMins === null ||
              newVersion.expectedDurationMins === undefined
              ? ''
              : newVersion.expectedDurationMins,
          isActive: newVersion.isActive,
          isGlobalTemplate: newVersion.isGlobalTemplate,
          requiresApproval: newVersion.requiresApproval ?? false,
        },
        steps: sortedNewSteps,
      }),
    );
    setIsDirty(false);
  };

  const handleDuplicateVersion = () => {
    if (isDirty) {
      setPendingDuplicate(true);
      setIsUnsavedChangesDialogOpen(true);
    } else {
      handleDuplicateVersionInternal();
    }
  };

  const handleActivateVersion = () => {
    if (!template || !selectedVersion) return;
    const updatedVersions = template.versions.map(v =>
      v.id === selectedVersion.id
        ? { ...v, isActive: true }
        : { ...v, isActive: false },
    );
    const updatedTemplate: TaskTemplateDto = {
      ...template,
      versions: updatedVersions,
    };
    setTemplate(updatedTemplate);
    setVersionFormValues(v => ({
      ...v,
      isActive: true,
    }));
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(steps);
    const [removed] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, removed);
    setSteps(items);
  };

  const openCreateStep = () => {
    setEditingStep(null);
    setStepFormValues({
      stepType: 'text',
      instruction: '',
      linkedDocumentId: null,
      linkedDocumentName: null,
      linkedUrl: '',
      linkedUrlLabel: '',
      evidenceRequired: false,
      evidenceType: '',
    });
    setIsStepModalOpen(true);
  };

  const openEditStep = (step: TaskTemplateStepDto) => {
    setEditingStep(step);
    setStepFormValues({
      stepType: step.stepType ?? 'text',
      instruction: step.instruction,
      linkedDocumentId: step.linkedDocumentId ?? null,
      linkedDocumentName: step.linkedDocumentName ?? null,
      linkedUrl: step.linkedUrl ?? '',
      linkedUrlLabel: step.linkedUrlLabel ?? '',
      evidenceRequired: step.evidenceRequired,
      evidenceType: step.evidenceType ?? '',
    });
    setIsStepModalOpen(true);
  };

  const handleDeleteStep = (stepId: number) => {
    setSteps(prev => prev.filter(s => s.id !== stepId));
  };

  const handleRequestDeleteStep = (stepId: number) => {
    setStepIdPendingDelete(stepId);
    setIsDeleteStepDialogOpen(true);
  };

  const handleConfirmDeleteStep = () => {
    if (stepIdPendingDelete !== null) {
      handleDeleteStep(stepIdPendingDelete);
    }
    setIsDeleteStepDialogOpen(false);
    setStepIdPendingDelete(null);
  };

  const handleCancelDeleteStep = () => {
    setIsDeleteStepDialogOpen(false);
    setStepIdPendingDelete(null);
  };

  const handleSaveStep = () => {
    const nextId = steps.length ? Math.max(...steps.map(s => s.id)) + 1 : 1;
    const evidenceType = stepFormValues.evidenceRequired && stepFormValues.evidenceType
      ? stepFormValues.evidenceType
      : null;

    if (editingStep) {
      setSteps(prev => prev.map(s => (s.id === editingStep.id ? {
        ...s,
        stepType: stepFormValues.stepType,
        instruction: stepFormValues.instruction,
        linkedDocumentId: stepFormValues.linkedDocumentId,
        linkedDocumentName: stepFormValues.linkedDocumentName,
        linkedUrl: stepFormValues.linkedUrl || null,
        linkedUrlLabel: stepFormValues.linkedUrlLabel || null,
        evidenceRequired: stepFormValues.evidenceRequired,
        evidenceType,
      } : s)));
    } else {
      const newStep: TaskTemplateStepDto = {
        id: nextId,
        order: steps.length + 1,
        stepType: stepFormValues.stepType,
        instruction: stepFormValues.instruction,
        linkedDocumentId: stepFormValues.linkedDocumentId,
        linkedDocumentName: stepFormValues.linkedDocumentName,
        linkedUrl: stepFormValues.linkedUrl || null,
        linkedUrlLabel: stepFormValues.linkedUrlLabel || null,
        evidenceRequired: stepFormValues.evidenceRequired,
        evidenceType,
      };
      setSteps(prev => [...prev, newStep]);
    }
    setIsStepModalOpen(false);
  };

  const handleConfirmDiscardChanges = () => {
    setIsUnsavedChangesDialogOpen(false);
    setIsDirty(false);
    if (pendingVersionChangeId !== null) {
      handleVersionChangeInternal(pendingVersionChangeId);
      setPendingVersionChangeId(null);
    }
    if (pendingDuplicate) {
      handleDuplicateVersionInternal();
      setPendingDuplicate(false);
    }
  };

  const handleCancelDiscardChanges = () => {
    setIsUnsavedChangesDialogOpen(false);
    setPendingVersionChangeId(null);
    setPendingDuplicate(false);
  };

  return (
    <PageArea>
      <Grid
        container
        justifyContent='space-between'
        alignItems='center'
        sx={{ mb: 3 }}
      >
        <Grid item>
          <PageTitle>{title}</PageTitle>
        </Grid>
        <Grid item>
          <Grid
            container
            spacing={2}
          >
            <Grid item>
              <Button
                variant='secondary'
                onClick={() => navigate('/task-templates')}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant='primary'
                onClick={handleSave}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={4}
      >
        <Grid
          item
          xs={12}
          md={12}
        >
          <Section style={{ paddingLeft: 8, paddingRight: 8, marginBottom: 8, paddingBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>Template Details</h3>
              {!isCreate && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Pill text={`Schedules: ${schedules.length}`} />
                  <Pill text={`Operational days: ${operationalDaysCount}`} />
                </div>
              )}
            </div>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  label='Name'
                  fullWidth
                  value={formValues.name}
                  onChange={e => setFormValues(v => ({ ...v, name: e.target.value }))}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  select
                  label='Category'
                  fullWidth
                  value={formValues.taskCategoryId}
                  onChange={e => setFormValues(v => ({ ...v, taskCategoryId: e.target.value === '' ? '' : Number(e.target.value) }))}
                >
                  <MenuItem value=''>None</MenuItem>
                  {categories.map(c => (
                    <MenuItem
                      key={c.id}
                      value={c.id}
                    >
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid
                item
                xs={12}
              >
                <TextField
                  label='Description'
                  fullWidth
                  multiline
                  minRows={3}
                  value={formValues.description}
                  onChange={e => setFormValues(v => ({ ...v, description: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Section>

          <Section style={{ paddingLeft: 8, paddingRight: 8, marginBottom: 8, paddingBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <h3>Version Details</h3>
                {!isCreate && template && (
                  <TextField
                    select
                    size='small'
                    value={selectedVersionId}
                    onChange={e => handleVersionChange(Number(e.target.value))}
                    style={{ minWidth: 160, marginBottom: 0 }}
                  >
                    {template.versions.map(v => (
                      <MenuItem
                        key={v.id}
                        value={v.id}
                      >
                        {`v${v.id}${v.isActive ? ' (Active)' : ''}`}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </div>
              {!isCreate && template && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {!selectedVersion?.isActive && (
                    <Button
                      variant='secondary'
                      onClick={handleActivateVersion}
                    >
                      Set Active
                    </Button>
                  )}
                  <Button
                    variant='secondary'
                    onClick={handleDuplicateVersion}
                  >
                    New Version
                  </Button>
                </div>
              )}
            </div>
            {selectedVersion ? (
              <Grid
                container
                spacing={2}
              >
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <TextField
                    select
                    label='Priority'
                    fullWidth
                    value={versionFormValues.defaultTaskPriorityId}
                    onChange={e =>
                      setVersionFormValues(v => ({
                        ...v,
                        defaultTaskPriorityId:
                          e.target.value === '' ? '' : Number(e.target.value),
                      }))
                    }
                    error={!!errors.defaultTaskPriorityId}
                    helperText={errors.defaultTaskPriorityId}
                  >
                    <MenuItem value=''>Select priority</MenuItem>
                    {priorities.map(p => (
                      <MenuItem
                        key={p.id}
                        value={p.id}
                      >
                        {p.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <TextField
                    label='Expected Duration (mins)'
                    type='number'
                    fullWidth
                    value={versionFormValues.expectedDurationMins}
                    onChange={e =>
                      setVersionFormValues(v => ({
                        ...v,
                        expectedDurationMins:
                          e.target.value === '' ? '' : Number(e.target.value),
                      }))
                    }
                    error={!!errors.expectedDurationMins}
                    helperText={errors.expectedDurationMins}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={versionFormValues.requiresApproval}
                        onChange={e => {
                          setVersionFormValues(v => ({
                            ...v,
                            requiresApproval: e.target.checked,
                          }));
                        }}
                      />
                    }
                    label='Requires Approval'
                  />
                </Grid>
              </Grid>
            ) : (
              <p>No version selected.</p>
            )}

            <Section style={{ paddingLeft: 8, paddingRight: 8, marginBottom: 8, paddingBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3>Steps</h3>
                <Button
                  variant='secondary'
                  onClick={openCreateStep}
                >
                  Add Step
                </Button>
              </div>
              {steps.length === 0 ? (
                <p>No steps for this version.</p>
              ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId='steps'>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {steps.map((step, index) => (
                          <Draggable
                            key={step.id}
                            draggableId={step.id.toString()}
                            index={index}
                          >
                            {(dragProvided) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '8px 12px',
                                  marginBottom: 8,
                                  border: '1px solid #ddd',
                                  borderRadius: 4,
                                  backgroundColor: '#fff',
                                  cursor: 'pointer',
                                  ...dragProvided.draggableProps.style,
                                }}
                                onClick={() => openEditStep(step)}
                              >
                                <div
                                  {...dragProvided.dragHandleProps}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginRight: 8,
                                    color: '#888',
                                    cursor: 'grab',
                                  }}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <DragIndicatorIcon fontSize='small' />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <strong>{index + 1}.</strong>{' '}
                                  {step.stepType === 'document' ? (
                                    <Box
                                      component="span"
                                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                    >
                                      <DescriptionIcon
                                        fontSize="small"
                                        color="primary"
                                      />
                                      <Typography
                                        component="span"
                                        variant="textSM"
                                        color="primary"
                                      >
                                        {step.linkedDocumentName || `Document #${step.linkedDocumentId}`}
                                      </Typography>
                                    </Box>
                                  ) : step.stepType === 'url' ? (
                                    <Box
                                      component="span"
                                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                    >
                                      <LinkIcon
                                        fontSize="small"
                                        color="secondary"
                                      />
                                      <Typography
                                        component="span"
                                        variant="textSM"
                                        color="secondary"
                                      >
                                        {step.linkedUrlLabel || step.linkedUrl}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    step.instruction
                                  )}
                                  {step.evidenceRequired && (
                                    <div style={{ fontSize: 12, color: '#666' }}>
                                      Evidence: {step.evidenceType || 'Required'}
                                    </div>
                                  )}
                                </div>
                                <div
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleRequestDeleteStep(step.id);
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: 8,
                                    color: '#c00',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize='small' />
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </Section>

          </Section>


        </Grid>
      </Grid>

      <Dialog
        open={isStepModalOpen}
        onClose={() => setIsStepModalOpen(false)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>{editingStep ? 'Edit Step' : 'Add Step'}</DialogTitle>
        <DialogContent>
          <FormControl
            component="fieldset"
            sx={{ mt: 2, mb: 2 }}
          >
            <FormLabel component="legend">Step Type</FormLabel>
            <RadioGroup
              row
              value={stepFormValues.stepType}
              onChange={e => {
                const newType = e.target.value as StepType;
                setStepFormValues(v => ({
                  ...v,
                  stepType: newType,
                  // Clear fields when switching types
                  instruction: newType === 'text' ? v.instruction : '',
                  linkedDocumentId: newType === 'document' ? v.linkedDocumentId : null,
                  linkedDocumentName: newType === 'document' ? v.linkedDocumentName : null,
                  linkedUrl: newType === 'url' ? v.linkedUrl : '',
                  linkedUrlLabel: newType === 'url' ? v.linkedUrlLabel : '',
                }));
              }}
            >
              <FormControlLabel
                value="text"
                control={<Radio />}
                label="Free Text Instruction"
              />
              <FormControlLabel
                value="document"
                control={<Radio />}
                label="Link to Document"
              />
              <FormControlLabel
                value="url"
                control={<Radio />}
                label="External URL"
              />
            </RadioGroup>
          </FormControl>

          {stepFormValues.stepType === 'text' && (
            <TextField
              label='Instruction'
              fullWidth
              multiline
              minRows={2}
              margin='normal'
              value={stepFormValues.instruction}
              onChange={e => setStepFormValues(v => ({ ...v, instruction: e.target.value }))}
            />
          )}

          {stepFormValues.stepType === 'document' && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography
                variant="textSM"
                sx={{ mb: 1, display: 'block' }}
              >
                Select Document
              </Typography>
              <DocumentLinker
                linkedDocumentIds={stepFormValues.linkedDocumentId ? [stepFormValues.linkedDocumentId] : []}
                onChange={(ids) => {
                  const docId = ids.length > 0 ? ids[0] : null;
                  setStepFormValues(v => ({
                    ...v,
                    linkedDocumentId: docId,
                  }));
                }}
              />
              {stepFormValues.linkedDocumentId && (
                <Box
                  sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <DescriptionIcon
                    fontSize="small"
                    color="primary"
                  />
                  <Typography variant="textSM">
                    Document ID: {stepFormValues.linkedDocumentId}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {stepFormValues.stepType === 'url' && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <TextField
                label='URL'
                fullWidth
                margin='normal'
                value={stepFormValues.linkedUrl}
                onChange={e => setStepFormValues(v => ({ ...v, linkedUrl: e.target.value }))}
                placeholder='https://example.com/resource'
                helperText='Enter the full URL including https://'
              />
              <TextField
                label='Display Label (optional)'
                fullWidth
                margin='normal'
                value={stepFormValues.linkedUrlLabel}
                onChange={e => setStepFormValues(v => ({ ...v, linkedUrlLabel: e.target.value }))}
                placeholder='Click here to view instructions'
                helperText='A friendly label to display instead of the URL'
              />
            </Box>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={stepFormValues.evidenceRequired}
                onChange={e => setStepFormValues(v => ({ ...v, evidenceRequired: e.target.checked, evidenceType: e.target.checked ? v.evidenceType : '' }))}
              />
            }
            label='Evidence required'
          />
          {stepFormValues.evidenceRequired && (
            <TextField
              select
              label='Evidence Type'
              fullWidth
              margin='normal'
              value={stepFormValues.evidenceType}
              onChange={e => setStepFormValues(v => ({ ...v, evidenceType: e.target.value as EvidenceType | '' }))}
            >
              <MenuItem value=''>Select evidence type</MenuItem>
              {evidenceTypeOptions.map(opt => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsStepModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleSaveStep}
            disabled={
              (stepFormValues.stepType === 'text' && !stepFormValues.instruction.trim()) ||
              (stepFormValues.stepType === 'document' && !stepFormValues.linkedDocumentId) ||
              (stepFormValues.stepType === 'url' && !stepFormValues.linkedUrl.trim())
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isUnsavedChangesDialogOpen}
        onClose={handleCancelDiscardChanges}
      >
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          You have unsaved changes on this version. If you continue, those changes will be lost.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDiscardChanges}>Cancel</Button>
          <Button
            variant='primary'
            onClick={handleConfirmDiscardChanges}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteStepDialogOpen}
        onClose={handleCancelDeleteStep}
      >
        <DialogTitle>Delete step?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this step?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteStep}>Cancel</Button>
          <Button
            variant='primary'
            onClick={handleConfirmDeleteStep}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default TaskTemplateDetails;
